/**
 * GDPR data export download (ADM-2).
 *
 * `POST /api/user-profile/export-data` returns
 * `{ export_data, exported_at, export_format: 'json' }`. The profile screen
 * turns `export_data` into a JSON file the browser downloads.
 */

const ISO_DATE_PREFIX = /^(\d{4}-\d{2}-\d{2})/;

/** `mcai-export-YYYY-MM-DD.json`, dated by the server's `exported_at` when it parses. */
export function buildExportFilename(exportedAt?: string | null, now: Date = new Date()): string {
  const match = typeof exportedAt === 'string' ? ISO_DATE_PREFIX.exec(exportedAt) : null;
  const date = match ? match[1] : now.toISOString().slice(0, 10);
  return `mcai-export-${date}.json`;
}

/** Pretty-printed JSON so the file is readable when opened directly. */
export function serializeExport(exportData: unknown): string {
  return JSON.stringify(exportData ?? {}, null, 2);
}

export function buildExportBlob(exportData: unknown): Blob {
  return new Blob([serializeExport(exportData)], { type: 'application/json' });
}

/**
 * Download a Blob as `filename` through a temporary object URL and anchor.
 * Browser-only; the object URL is revoked after the click.
 */
export function downloadBlob(blob: Blob, filename: string, doc: Document = document): void {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = doc.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    doc.body.appendChild(anchor);
    anchor.click();
    doc.body.removeChild(anchor);
  } finally {
    // Defer the revoke so the browser has started reading the blob.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
